import PageHero from '@/components/PageHero'
import SEOHead from '@/components/SEOHead'
import {CheckCircle2, Mail} from 'lucide-react'
import { useState, type FormEvent } from 'react'

function Contact() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !email.trim() || !message.trim()) return
    // Mock submission handler — no backend wired up.
    setSubmitted(true)
    setName('')
    setEmail('')
    setSubject('')
    setMessage('')
  }

  const fieldClass = 'w-full rounded-lg border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 focus:bg-white'

  return (
    <div>
      <SEOHead
        title="Contact Us"
        description="Get in touch with the AdsROAS Calculator team for questions, feedback, or partnership inquiries."
      />

      <PageHero
        eyebrow="Support"
        title="Contact Us"
        description="Questions, feedback, or partnership inquiries? We would love to hear from you."
      />

      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14">
        {submitted ? (
          <div className="bg-white border border-emerald-100 rounded-2xl p-8 sm:p-10 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="mt-4 text-xl font-extrabold text-slate-900 tracking-tight">Message Sent</h2>
            <p className="mt-2 text-[15px] text-slate-600">
              Thanks for reaching out. We typically respond within 1-2 business days.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-6 inline-flex items-center bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold px-5 py-2.5 rounded-lg shadow-sm shadow-emerald-600/20 transition-colors"
            >
              Send Another Message
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="flex items-center gap-2 text-emerald-700 pb-1">
              <Mail className="w-4 h-4" />
              <span className="text-[13px] font-medium">We usually reply within 1-2 business days</span>
            </div>

            <div>
              <label htmlFor="name" className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500 mb-1.5">Name</label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={fieldClass}
                placeholder="Jane Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500 mb-1.5">Email</label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={fieldClass}
                placeholder="jane@company.com"
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500 mb-1.5">Subject</label>
              <input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className={fieldClass}
                placeholder="Question about ROAS calculations"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500 mb-1.5">Message</label>
              <textarea
                id="message"
                required
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className={`${fieldClass} resize-none`}
                placeholder="How can we help?"
              />
            </div>

            <button
              type="submit"
              className="w-full inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold px-5 py-3 rounded-lg shadow-sm shadow-emerald-600/20 transition-colors"
            >
              Send Message
            </button>
          </form>
        )}
      </section>
    </div>
  )
}

export default Contact
